// ─────────────────────────────────────────────────────────────────────────────
// Price Observation Models
// ─────────────────────────────────────────────────────────────────────────────
// Interfaces and types for the pricing-observations domain.
// Matches the backend shapes of /ai/kb/pricing-observations.
//
// A price observation is one price read out of one source document, carried
// with the file, the row and the verbatim line it came from. Never averaged or
// derived, and deletable line by line.
// ─────────────────────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────────────────────

import { IPagination } from 'src/app/shared/models/page.model';

/**
 * Who the price applies to.
 * `unresolved` means the customer could not be determined — the row is visible
 * and filterable but never served to the agent.
 */
export type PriceObservationScope = 'customer' | 'group' | 'global' | 'unresolved';

/**
 * Which attribution channel produced the customer. `domain` is the weak
 * channel and stays identifiable on purpose: one domain can map several
 * customers.
 */
export type PriceObservationResolvedBy = 'column' | 'email' | 'domain' | 'scope' | 'manual' | 'none';

/**
 * - `matched`    — usable, and the only state ever served to the agent.
 * - `to_verify`  — the product did not resolve; an operator has to pick it.
 * - `conflict`   — two prices quoted the same day; neither is published.
 * - `superseded` — a newer observation took over the same key.
 * - `duplicate`  — the strict re-import of a price already known.
 * - `rejected`   — kept in the enum; nothing writes it any more.
 */
export type PriceObservationState = 'matched' | 'to_verify' | 'conflict' | 'superseded' | 'duplicate' | 'rejected';

/**
 * Which import channel an observation was read from.
 *
 * Deliberately not `PriceSourceKind`: settings uses that name for which engine
 * source a price comes from, and two string unions under one name is an import
 * TypeScript cannot object to.
 */
export type PriceImportSourceKind = 'price_file' | 'email_export' | 'manual';

/** Which unit the price is expressed in. `null` when the source did not say. */
export type PriceUnitBasis = 'base_unit' | 'selling_unit' | null;

/** One handling unit of the row's product, as the correction dialog offers it. */
export interface IObservationUnitOption {
  id: number;
  name: string;
  is_base_unit: boolean;
}

/**
 * One matcher suggestion for an unresolved product. `product_id` is the local
 * template behind the sku — null when the sku is not in this catalogue, in
 * which case the candidate is shown but cannot be applied in one click.
 */
export interface IPriceMatchCandidate {
  sku: string;
  name: string | null;
  confidence: number | null;
  method: string | null;
  product_id: number | null;
}

export type PriceImportState =
  | 'pending'
  | 'running'
  // A message carried a tariff whose layout follows neither the template nor a
  // mapping. Not a failure: the file was read, its columns are just unnamed,
  // and the journal offers the mapping question on the row.
  | 'awaiting_mapping'
  | 'done'
  | 'failed';

/**
 * The single status shown by the list, the tooltip and the status filter.
 *
 * Derived server-side from (state, is_current, scope, freshness) and never
 * re-derived here: two formulas for "expired" would let a row read Active on
 * one screen and Expired on another. `state` stays on the model as the raw
 * fact; `status` is what a human is shown.
 */
export type PriceObservationStatus =
  | 'active'
  | 'expired'
  | 'superseded'
  | 'duplicate'
  | 'to_verify'
  | 'unknown_customer';

/**
 * The values of each union, in the order an operator is shown them.
 *
 * The unions above say what is legal; these say what the UI offers. Kept beside
 * their type so a value added to one is a compile error away from the other.
 */
export const PRICE_OBSERVATION_STATUSES: PriceObservationStatus[] = [
  'active',
  'expired',
  'superseded',
  'duplicate',
  'to_verify',
  'unknown_customer',
];

export const PRICE_OBSERVATION_SCOPES: PriceObservationScope[] = [
  'customer',
  'group',
  'global',
  'unresolved',
];

export const PRICE_OBSERVATION_SOURCE_KINDS: PriceImportSourceKind[] = [
  'price_file',
  'email_export',
  'manual',
];

/**
 * No filter set — one description of that, shared by the list and the store's
 * initial state.
 */
export const EMPTY_PRICE_OBSERVATION_FILTERS: Partial<IPriceObservationFilters> = {
  import_id: null,
  customer_id: null,
  product_id: null,
  state: null,
  scope: null,
  is_current: null,
  // The derived status: the backend turns it into a domain, so the filter and
  // the badge answer the same question.
  status: null,
  source_kind: null,
  observed_from: null,
  observed_to: null,
};


// ── Interfaces ───────────────────────────────────────────────────────────────

export interface IPriceObservation {
  id: number;

  // Audience
  scope: PriceObservationScope;
  resolved_by: PriceObservationResolvedBy;
  customer_id: number | null;
  customer_name: string | null;
  group_ids: number[];
  /** The audience's names for a group-scoped tariff. */
  group_names: string[];
  /** The third party's raw address — what resolving the customer writes to the registry. */
  counterparty_email: string | null;

  // Product
  product_id: number | null;
  product_name: string | null;
  /** The recognised product's own code — what "CODE — Name" renders. */
  product_sku: string | null;
  product_key: string;
  product_name_raw: string | null;
  /** The code the customer orders under. */
  customer_code_raw: string | null;
  /**
   * The code attaching a product would register as an external reference,
   * computed server-side (`_learned_code`). Null when the row was keyed on a
   * designation — that teaches an alias, which is not what the dialog asks
   * about. Never re-derived here: two formulas would promise a reference the
   * server does not write.
   */
  learned_code: string | null;

  // Value
  price: number;
  currency_id: number | null;
  currency: string | null;
  price_is_tax_excluded: boolean;
  /** The unit as written in the document — the evidence, matched by name. */
  unit: string | null;
  unit_basis: PriceUnitBasis;
  /**
   * The units this row's product actually sells in.
   *
   * The set the backend matches `unit` against, so what the correction dialog
   * offers can never be a unit the resolution would refuse. Empty while the
   * product is unresolved: there is no catalogue to read units off yet.
   */
  available_units?: IObservationUnitOption[];

  // Validity
  observed_at: string | null;
  valid_until: string | null;

  // Provenance — read-only. An observation whose quote could be edited would
  // stop being evidence, so the backend refuses to write these.
  source_kind: PriceImportSourceKind;
  /**
   * The file this line was read from, as a handle the preview route resolves:
   * the spreadsheet that was dropped, the body of one message of an export, or
   * a document attached to that message. Null when nothing previewable was
   * kept (an archive whose member yielded no render).
   */
  document_id: number | null;
  source_filename: string | null;
  source_sheet: string | null;
  source_row: number | null;
  source_message_id: string | null;
  source_quote: string | null;
  import_id: number | null;

  state: PriceObservationState;
  is_current: boolean;
  /** Derived server-side. The front renders it, never computes it. */
  status: PriceObservationStatus;
  /**
   * How the line was read. 1.0 = deterministic rule or human entry;
   * below 1.0 = a model proposed it and the gates verified it — the row stays
   * `to_verify` until a human confirms, which is the only place it shows.
   */
  confidence: number;
  /**
   * Matcher suggestions for an unresolved product — suggestions only; the
   * operator applies one through the edit dialog, where it travels as
   * `product_template_id` like any human choice.
   */
  match_candidates: IPriceMatchCandidate[] | null;
  /** The correction is in place, and stamped. */
  corrected_manually: boolean;
  corrected_at: string | null;
  corrected_by: string | null;
  /**
   * The rival rows of a conflict; what the banner names.
   *
   * The source travels with the price: arbitrating between two amounts means
   * knowing which file each one was read from.
   */
  conflict_with: Array<{
    id: number;
    price: number;
    currency: string | null;
    observed_at: string | null;
    source_filename: string | null;
    source_row: number | null;
    source_quote: string | null;
  }> | null;
}

/** One row of the import journal — one file, with its report. */
export interface IPriceImport {
  id: number;
  filename: string;
  /** The uploaded payload, so the file name opens it. Null on an archive. */
  document_id: number | null;
  source_kind: PriceImportSourceKind;
  scope: PriceObservationScope | null;
  /** One tariff file can be published to several customers. */
  customer_ids: number[];
  customer_names: string[];
  state: PriceImportState;
  error: string | null;
  count_created: number;
  count_to_verify: number;
  count_rejected: number;
  /** Machine reasons, `{reason: count}` — translated by key in the UI. */
  reasons: Record<string, number>;
  observation_count: number;
  create_date: string | null;
}

/**
 * What POST /import answers with — one row per uploaded file.
 *
 * Not an `IPriceImport`: the upload route answers with the journal's *report*
 * shape (`import_id`, flat counters), while the journal list answers with the
 * serialised record (`id`, `count_*`).
 *
 * The call returns 202: these rows are `pending`, and the prices exist only
 * once the extraction has run.
 */
export interface IPriceImportReceipt {
  import_id: number;
  filename: string;
  state: PriceImportState;
  created: number;
  to_verify: number;
  rejected: number;
  reasons: Record<string, number>;
}

/** The states an import is still working through. */
export const PRICE_IMPORT_PENDING_STATES: PriceImportState[] = ['pending', 'running'];

export interface IPriceObservationFilters {
  import_id: number | null;
  customer_id: number | null;
  product_id: number | null;
  state: PriceObservationState | null;
  scope: PriceObservationScope | null;
  is_current: boolean | null;
  /** The derived status, translated to a domain by the backend. */
  status: PriceObservationStatus | null;
  source_kind: PriceImportSourceKind | null;
  observed_from: string | null;
  observed_to: string | null;
}

/**
 * Only the value and attachment fields. Provenance is not writable.
 *
 * The edit dialog also moves the customer, the product and the scope. The
 * product travels as `product_template_id` — the template id, the only form
 * the backend accepts (a bare `product_id` is refused).
 */
export interface IUpdatePriceObservationPayload {
  price?: number;
  currency_id?: number;
  unit?: string | null;
  unit_basis?: PriceUnitBasis;
  observed_at?: string | null;
  valid_until?: string | null;
  price_is_tax_excluded?: boolean;
  customer_code_raw?: string | null;
  product_name_raw?: string | null;
  state?: PriceObservationState;
  customer_id?: number | null;
  product_template_id?: number;
  scope?: PriceObservationScope;
  /** A bare list of ids; the server turns it into the m2m command. */
  group_ids?: number[];
  /**
   * Whether attaching the product also registers the customer's code as an
   * external reference. Absent means yes, for callers that do not ask.
   */
  remember_reference?: boolean;
}

/** The list page's pagination, plus the three review counters it carries. */
export interface IPriceObservationMetadata extends IPagination {
  unresolved: number;
  to_verify: number;
  conflict: number;
}

// ── Preview (dry run) ────────────────────────────────────────────────────────

/**
 * One sampled line of the preview — a sample, not a line-by-line dry run: the
 * full extraction is deferred, so previewing every line would promise what has
 * not been read yet.
 */
export interface IPriceObservationPreviewRow {
  product_code: string;
  product_name: string;
  price: string | null;
  currency: string | null;
  unit: string | null;
  observed_at: string | null;
  valid_until: string | null;
  scope: PriceObservationScope;
  resolved_by: PriceObservationResolvedBy;
  customer_id: number | null;
  customer_name: string | null;
  product_id: number | null;
  product_id_name: string | null;
  state: PriceObservationState;
  reason: string | null;
  source_filename: string | null;
  quote: string;
}

export interface IPriceObservationPreviewSummary {
  /** Whole-file counts, exact: the sample table is a glimpse, these are not. */
  rows_processed: number;
  lines_extracted?: number;
  messages?: number;
  resolved_customer?: number;
  resolved_product?: number;
  /** The lines that will land "Unknown customer, to correct". */
  unknown_customer?: number;
  sample_size?: number;
}

export interface IPriceObservationPreview {
  /** Set when the file was refused (template mismatch): the server names the
   *  columns. */
  missing_columns?: string[] | null;
  expected_columns?: string[] | null;
  filename: string;
  source_kind: PriceImportSourceKind;
  /**
   * False for an email export: such a batch is multi-customer by nature, so a
   * batch-level scope would overwrite the real per-message attribution. The
   * dialog hides the scope step when this is false.
   */
  scope_allowed: boolean;
  sample: IPriceObservationPreviewRow[];
  summary: IPriceObservationPreviewSummary;
  error?: string;
  reason?: string | null;
}

/**
 * Who an uploaded batch is for, asked once at import.
 *
 * `file` is not an audience but a deferral — the file's `customer` column names
 * the customer of each row, resolved server-side by code — and it is the
 * default; nothing is sent for it, the absence of a scope is the answer.
 *
 * The other three are the same three the knowledge base offers on a document,
 * and the same three an observation can carry. They exist for a file with no
 * customer column, such as a standalone PDF price list.
 */
export type PriceImportAudience = 'file' | 'global' | 'customer' | 'group';

export interface IPriceImportScopeSelection {
  scope: PriceImportAudience | null;
  /**
   * Several, like the knowledge base's own upload. Each named customer gets its
   * own price rows: `customer_id` stays a Many2one on the observation, which is
   * the slot the merge arbitrates.
   */
  customer_ids?: number[];
  group_ids?: number[];
}

// ── Template conformity, and the mapping a refusal opens ─────────────────────

/**
 * A field the reader can be told about — one select on the mapping screen.
 *
 * There is no `ignore`: the screen asks per field, so a column nobody chose is
 * simply named by nothing.
 */
export type PriceColumnRole =
  | 'product_code'
  | 'product_name'
  | 'price'
  | 'currency'
  | 'unit'
  | 'customer_code'
  | 'customer'
  | 'price_date'
  | 'valid_until';

/** The confirmed reading of one file: one column index per field. */
export interface IPriceColumnMapping {
  /**
   * Keyed by field, valued by the column index that holds it. A field the
   * operator left empty is absent, not null: the server reads the keys.
   */
  fields: Partial<Record<PriceColumnRole, number>>;
  header_row: number | null;
}

/**
 * The drop-step answer: is this file importable as dropped? For a spreadsheet,
 * conform to the template or not — with the missing columns named by the
 * server, the only owner of that list.
 *
 * A refusal is not a dead end. When `conform` is false the same answer carries
 * what the mapping screen needs to ask its question: the header row the server
 * detected, its labels, a sample of the file and the fields to ask about. One
 * route, one reading of the file.
 *
 * Nothing is pre-filled: the mapping screen opens blank.
 */
export interface IPriceImportInspection {
  filename: string;
  source_kind: 'price_file' | 'email_export';
  conform: boolean;
  missing_columns: string[];
  expected_columns: string[];

  /** Present only on a refusal a mapping could answer. */
  header_row?: number;
  labels?: string[];
  /** Body rows, below the detected header. */
  sample?: string[][];
  /** The fields to ask about, in the order the screen lists them. */
  roles?: PriceColumnRole[];
}

/**
 * One attached spreadsheet an import is waiting on, as
 * `GET /imports/<id>/mapping` returns it.
 *
 * The same three fields the drop screen's refusal carries (`header_row`,
 * `labels`, `sample`), so the mapping step renders it unchanged: it is the
 * same question, asked later about a file that arrived inside a message.
 */
export interface IPriceImportMappingQuestion {
  attachment_id: number;
  filename: string;
  header_row: number;
  labels: string[];
  sample: string[][];
}

export interface IPriceImportMappingAsk {
  import_id: number;
  state: PriceImportState;
  questions: IPriceImportMappingQuestion[];
  expected_columns: string[];
  roles: PriceColumnRole[];
}

// ── Row formatting ───────────────────────────────────────────────────────────
// The two labels every surface of an observation writes — shared so the list
// and the edit dialog can never name a row two different ways.

/** "CODE — Name" when the product is recognised; the raw text otherwise. */
export function productLabel(row: IPriceObservation): string {
  if (row.product_id && row.product_name) {
    return row.product_sku ? `${row.product_sku} — ${row.product_name}` : row.product_name;
  }
  return row.product_name_raw ?? row.product_key;
}

/** "file.xlsx · 42" — the file the price was read from, and where in it. */
export function sourceLabel(row: IPriceObservation, empty = '—'): string {
  if (!row.source_filename) return empty;
  return row.source_row ? `${row.source_filename} · ${row.source_row}` : row.source_filename;
}

/** "SKU — Name" for a match candidate; the sku alone when it carries no name. */
export function candidateLabel(candidate: IPriceMatchCandidate): string {
  return candidate.name ? `${candidate.sku} — ${candidate.name}` : candidate.sku;
}

/**
 * A machine reason — the ones a price line carries — as the operator reads it.
 *
 * Named once, here: the journal and the import preview show the same reasons,
 * and a second copy of the lookup would be a second wording. Falls back to the
 * raw key rather than a blank, so a reason the front has not translated yet
 * still answers "why did this file produce nothing".
 */
export function machineReasonLabel(
  translate: { instant(key: string): string },
  reason: string,
  count = 1,
): string {
  const key = `knowledge_base.price_observations.reason.${reason}`;
  const translated = translate.instant(key);
  const label = translated !== key ? translated : reason;
  return count > 1 ? `${label} (${count})` : label;
}
